import React, { useEffect } from "react";
import { Box, Card, Divider, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { TEXT } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import { trackEvent } from "../../services/GoogleAnalytics";
import FooterContactUsCard from "./FooterContactUsCard";

const AboutUsMain = () => {
    const { t } = usePagesTranslation();

    useEffect(() => {
        trackEvent("entered", { name: "AboutUsMain" });
    }, []);

    useEffect(() => {
        const title = t("about.title", "About Us");
        document.title = `${title} | Puzzle Interact`;
        return () => {
            document.title = process.env.REACT_APP_SITE_NAME || "Puzzle Interact";
        };
    }, [t]);

    return (
        <Box
            sx={{
                width: "100%",
                minHeight: "100vh",
                py: 4,
            }}
        >
            <Box
                sx={{
                    maxWidth: "900px",
                    mx: "auto",
                    px: 3,
                }}
            >
                <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                        textAlign: "center",
                        mb: 1.5,
                        fontWeight: 700,
                        color: "#3BA666",
                    }}
                >
                    {t("about.title", "About Us")}
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        textAlign: "center",
                        mb: 4,
                        color: TEXT.secondary,
                        maxWidth: 640,
                        mx: "auto",
                        fontWeight: 600,
                    }}
                >
                    {t("about.slogan", "Solve, Connect, Understand")}
                </Typography>

                <Card sx={{ p: 3, mb: 3, boxShadow: 1 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                        {t(
                            "about.intro",
                            "We make puzzles that guide your logic — they help you see the next step without giving the answer away.",
                        )}
                    </Typography>
                </Card>

                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: TEXT.primary }}>
                    {t("about.missionTitle", "What we believe")}
                </Typography>
                <Card sx={{ p: 3, mb: 3, boxShadow: 1 }}>
                    <Typography variant="body1" color="text.secondary">
                        {t(
                            "about.missionBody",
                            "Most games treat words and pictures as isolated challenges. We connect them into stories and networks so each solve teaches you something — a new word, a hidden link, a clearer way to think.",
                        )}
                    </Typography>
                </Card>

                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: TEXT.primary }}>
                    {t("about.productsTitle", "What we make")}
                </Typography>
                <Card sx={{ p: 3, mb: 3, boxShadow: 1 }}>
                    <Typography variant="body1" color="text.secondary">
                        {t(
                            "about.productsBody",
                            "Play crossword, word search, hidden message, sliding, jigsaw, and word-wheel puzzles in your browser. Our mobile apps — Sliding Puzzle Quest, Jigsaw Muse, and Word Wheel Quest — are built for one-handed play on iPhone and Android.",
                        )}
                    </Typography>
                </Card>

                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: TEXT.primary }}>
                    {t("about.publishTitle", "What we publish")}
                </Typography>
                <Card sx={{ p: 3, mb: 3, boxShadow: 1 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
                        {t(
                            "about.publishBody",
                            "Puzzle Interact is an original puzzle publisher. We write illustrated stories in English, then build crossword, word search, hidden-message, sliding, and jigsaw puzzles from that same vocabulary and art. New stories are added as we publish — they are not scraped lists or stock-photo boards.",
                        )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <Link component={RouterLink} to="/stories" sx={{ fontWeight: 600, mr: 2 }}>
                            {t("stories.back-list", "All stories")}
                        </Link>
                        <Link component={RouterLink} to="/storybook" sx={{ fontWeight: 600, mr: 2 }}>
                            {t("nav.storybook", "Storybook")}
                        </Link>
                        <Link component={RouterLink} to="/contact" sx={{ fontWeight: 600 }}>
                            {t("landing.contact", "Contact Us")}
                        </Link>
                    </Typography>
                </Card>

                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: TEXT.primary }}>
                    {t("about.entityTitle", "Our company")}
                </Typography>
                <Card sx={{ p: 3, mb: 4, boxShadow: 1 }}>
                    <Typography variant="body1" color="text.secondary">
                        {t(
                            "about.entityBody",
                            "Puzzle Interact is the consumer brand of Jeux Puzzle Interact, based in Quebec, Canada. Questions: use Contact or email support@puzzleinteract.com.",
                        )}
                    </Typography>
                </Card>

                <Divider sx={{ my: 4 }} />

                <FooterContactUsCard t={t} />
            </Box>
        </Box>
    );
};

export default AboutUsMain;
